/**
 * tokenApi.ts — typed wrappers for token bundle purchases, tipping, and PPP.
 *
 * NOTE: apiClient.get/post already unwrap res.data, so the return value IS
 * the response body directly. Do NOT double-unwrap with res.data.data.
 */
import { apiClient } from './apiClient';

export interface TokenBundle {
  key: string;
  label: string;
  price: number;
  tokens: number;
  baseTokens: number; // same as tokens (no bonus tokens)
  bonusTokens: number; // always 0
  totalTokens: number; // same as tokens
  platformFee: number;
  pricePerToken: number;
}

export interface TipHistoryItem {
  id: number;
  tokenAmount: number;
  dollarValue?: number;
  message: string | null;
  createdAt: string;
  sender?: { id: number; username: string; displayName: string | null; handle: string | null };
  creator?: { id: number; username: string; displayName: string | null; handle: string | null };
  journal: { id: number; title: string; slug: string | null } | null;
}

export interface TipTotals {
  totalTokensReceived: number;
  totalTips: number;
  totalEarningsUsd: number;
}

/** Response shape from GET /api/tips/balance */
export interface TokenBalanceResponse {
  tokenBalance: number;
  /** ISO datetime of the next expiring bundle, or null if no expiring bundles. */
  nextExpiryDate: string | null;
  /** Token count of the next expiring bundle. */
  nextExpiryTokens: number | null;
}

// ── Bundles ───────────────────────────────────────────────────────────────────

export async function fetchTokenBundles(): Promise<TokenBundle[]> {
  const body = await apiClient.get<{ success: boolean; data: TokenBundle[] }>(
    '/payments/tokens/bundles',
  );
  return body.data ?? [];
}

// ── Purchase bundle ───────────────────────────────────────────────────────────

/**
 * Start a Stripe Checkout session for a token bundle. Optionally accepts a
 * promo or creator code — the backend re-validates the code against the
 * authenticated user and bundle, then either lowers the unit_amount (one-time
 * payment) or surfaces an error if the code doesn't apply.
 */
export async function purchaseTokenBundle(bundleKey: string, code?: string): Promise<string> {
  const body = await apiClient.post<{ success: boolean; data: { checkoutUrl: string } }>(
    '/payments/tokens/purchase',
    { bundleKey, ...(code ? { code } : {}) },
  );
  return body.data.checkoutUrl;
}

// ── Balance ───────────────────────────────────────────────────────────────────

export async function fetchTokenBalance(): Promise<number> {
  const body = await apiClient.get<{ success: boolean; data: TokenBalanceResponse }>(
    '/tips/balance',
  );
  return body.data?.tokenBalance ?? 0;
}

/**
 * Fetch the full balance response including expiry metadata.
 * Use when you need to show expiry warnings in the token wallet UI.
 */
export async function fetchTokenBalanceFull(): Promise<TokenBalanceResponse> {
  const body = await apiClient.get<{ success: boolean; data: TokenBalanceResponse }>(
    '/tips/balance',
  );
  return body.data ?? { tokenBalance: 0, nextExpiryDate: null, nextExpiryTokens: null };
}

// ── Pay-per-post with tokens ──────────────────────────────────────────────────

export interface TokenPPPResult {
  tokensSpent: number;
  newBalance: number;
  journalId: number;
  creatorEarningUsd: number;
}

export interface TokenPPPInsufficientError {
  code: 'INSUFFICIENT_TOKENS';
  tokensRequired: number;
  currentBalance: number;
  shortfall: number;
}

/**
 * Purchase a pay-per-post journal using tokens from the reader's balance.
 *
 * Token cost = ceil(journal.price / $0.075). The backend calculates the exact
 * cost — the frontend does not need to pre-compute it.
 *
 * On insufficient balance, throws with `code: 'INSUFFICIENT_TOKENS'` and
 * `shortfall` so the UI can prompt the reader to buy more tokens.
 */
export async function purchasePPPWithTokens(journalId: number): Promise<TokenPPPResult> {
  const body = await apiClient.post<{ success: boolean; data: TokenPPPResult }>(
    '/payments/purchase/tokens',
    { journalId },
  );
  return body.data;
}

/**
 * Preview how many tokens a journal purchase will cost before committing.
 * Useful for showing the cost in the buy button before the user clicks confirm.
 *
 * Token cost = ceil(price / 0.075). Returns null if price is unavailable.
 */
export function previewTokenCost(journalPriceUsd: number): number {
  const TOKEN_PPP_VALUE_USD = 0.075;
  return Math.ceil(journalPriceUsd / TOKEN_PPP_VALUE_USD);
}

// ── Send tip ──────────────────────────────────────────────────────────────────

export interface SendTipPayload {
  creatorId: number;
  tokenAmount: number;
  journalId?: number;
  message?: string;
}

export interface SendTipResult {
  tip: { id: number; tokenAmount: number; createdAt: string };
  newBalance: number;
}

export async function sendTip(payload: SendTipPayload): Promise<SendTipResult> {
  const body = await apiClient.post<{ success: boolean; data: SendTipResult }>(
    '/tips/send',
    payload,
  );
  return body.data;
}

// ── History ───────────────────────────────────────────────────────────────────

export async function fetchSentTips(
  cursor?: number,
  limit = 20,
): Promise<{ tips: TipHistoryItem[]; nextCursor: number | null }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', String(cursor));
  const body = await apiClient.get<{
    success: boolean;
    data: { tips: TipHistoryItem[]; nextCursor: number | null };
  }>(`/tips/history/sent?${params}`);
  return body.data;
}

export async function fetchReceivedTips(
  cursor?: number,
  limit = 20,
): Promise<{ tips: TipHistoryItem[]; nextCursor: number | null; totals: TipTotals }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', String(cursor));
  const body = await apiClient.get<{
    success: boolean;
    data: { tips: TipHistoryItem[]; nextCursor: number | null; totals: TipTotals };
  }>(`/tips/history/received?${params}`);
  return body.data;
}

// ── Newsletter usage ──────────────────────────────────────────────────────────

export interface NewsletterUsage {
  usedThisMonth: number;
  baseLimit: number | null; // null = Author (unlimited)
  overageBalance: number | null; // null = Author
  effectiveLimit: number | null; // null = Author
  isUnlimited: boolean;
  remaining: number | null; // null = Author
}

export async function fetchNewsletterUsage(): Promise<NewsletterUsage> {
  const body = await apiClient.get<{ success: boolean; data: NewsletterUsage }>(
    '/payments/newsletter/usage',
  );
  return body.data;
}

/**
 * Purchase newsletter overage packs.
 * Each pack = 1,000 sends at $2.00. Returns a Stripe Checkout URL.
 */
export async function purchaseNewsletterOverage(packs: number): Promise<string> {
  const body = await apiClient.post<{
    success: boolean;
    data: { checkoutUrl: string; totalSends: number };
  }>('/payments/newsletter/purchase-overage', { packs });
  return body.data.checkoutUrl;
}

// ── Creator payout ────────────────────────────────────────────────────────────

export interface CreatorBalance {
  availableBalance: number;
  totalEarnings: number;
  minimumPayoutUsd: number;
  canPayout: boolean;
}

export async function fetchCreatorBalance(): Promise<CreatorBalance> {
  const body = await apiClient.get<{ success: boolean; data: CreatorBalance }>(
    '/payments/connect/balance',
  );
  return body.data;
}

export interface PayoutResult {
  transferId: string;
  amountUsd: number;
  message: string;
}

export async function requestPayout(): Promise<PayoutResult> {
  const body = await apiClient.post<{ success: boolean; data: PayoutResult }>(
    '/payments/connect/request-payout',
    {},
  );
  return body.data;
}
